import React, { useState, useEffect, useCallback } from 'react';
import {
    Modal,
    Button,
    Input,
    Row,
    Col,
    Typography,
    Space,
    Progress,
    Spin,
    App, Divider, Switch
} from 'antd';

import NumbersKeypad from "./NumbersKeypad";
import {deviceCashierRoleService, devicePrintService, deviceSalesService} from "../../services/posApi";
import { terminalIntegrationService } from '../../services/terminalIntegration';

const { Title, Text } = Typography;

// Функция для проверки запуска в Electron
const isElectron = () => {
    return typeof window !== 'undefined' && window.process && window.process.type === 'renderer';
};

const SalePaymentModal = ({
                              visible,
                              onCancel,
                              onComplete,
                              totalAmount,
                              cart,
                              customer
                          }) => {
    const { message: messageApi, modal } = App.useApp();

    // Состояние модала
    const [paymentMethods, setPaymentMethods] = useState([]);
    const [selectedMethod, setSelectedMethod] = useState(null);
    const [paymentAmounts, setPaymentAmounts] = useState({});
    const [editedMethods, setEditedMethods] = useState(new Set()); // Отслеживаем отредактированные способы
    const [saleProcessing, setSaleProcessing] = useState(false);
    const [saleStep, setSaleStep] = useState(''); // 'creating', 'printing'
    const [receiptPolling, setReceiptPolling] = useState(null);
    const [ignoreIntegration, setIgnoreIntegration] = useState({});
    const [printTimeout, setPrintTimeout] = useState(null);
    const [printTimer, setPrintTimer] = useState(0);
    const [terminalProcessing, setTerminalProcessing] = useState(false);
    const [terminalStatus, setTerminalStatus] = useState('');
    const [terminalProcessId, setTerminalProcessId] = useState('');
    const [terminalPaymentDetails, setTerminalPaymentDetails] = useState(null);
    const [receiptCheckInProgress, setReceiptCheckInProgress] = useState(false);
    const [idempotencyKey, setIdempotencyKey] = useState(null);


    // Универсальная функция печати чека
    const printReceiptUniversal = useCallback(async (receiptHtml) => {
        try {
            if (isElectron()) {
                // Печать через Electron API
                await devicePrintService.printReceipt(receiptHtml);
            } else {
                // Печать через браузер
                const printWindow = window.open('', '_blank');
                if (printWindow) {
                    printWindow.document.write(receiptHtml);
                    printWindow.document.close();
                    printWindow.focus();
                    printWindow.print();
                    printWindow.close();
                } else {
                    throw new Error('Не удалось открыть окно печати');
                }
            }
        } catch (error) {
            console.error('Ошибка при печати чека:', error);
            throw error;
        }
    }, []);

    // Загрузка способов оплаты из localStorage
    const loadPaymentMethods = useCallback(() => {
        try {
            const deviceInfo = JSON.parse(localStorage.getItem('device_register_info') || '{}');
            const methods = deviceInfo?.payment_methods || [];

            // Добавляем баллы клиента если есть клиент с балансом
            if (customer && customer.balance > 0) {
                methods.push({
                    id: 'customer_points',
                    name: 'Баллы',
                    type: 'bonus',
                    balance: customer.balance
                });
            }

            setPaymentMethods(methods);

            if (methods.length > 0) {
                setSelectedMethod(methods[0]);
                setPaymentAmounts({ [methods[0].id]: totalAmount.toString() });
            }
        } catch (error) {
            console.error('Ошибка загрузки способов оплаты:', error);
            messageApi.error('Не удалось загрузить способы оплаты');
        }
    }, [messageApi, totalAmount, customer]);

    // Функции для управления флагом pay_card в localStorage
    const updatePayCardStatus = useCallback((status) => {
        try {
            const salesCart = JSON.parse(localStorage.getItem('sales_cart') || '{}');
            if (salesCart && typeof salesCart === 'object') {
                salesCart.pay_card = status;
                localStorage.setItem('sales_cart', JSON.stringify(salesCart));
            }
        } catch (error) {
            console.error('Ошибка при обновлении pay_card статуса:', error);
        }
    }, []);

    // Инициализация при открытии модала
    useEffect(() => {
        if (visible) {
            loadPaymentMethods();
            setEditedMethods(new Set());
            setIgnoreIntegration({});
            setIdempotencyKey(null); // Сброс ключа при открытии
            setReceiptCheckInProgress(false);
        }
    }, [visible, loadPaymentMethods]);

    // Очистка таймера при закрытии
    useEffect(() => {
        return () => {
            if (receiptPolling) {
                clearInterval(receiptPolling);
            }
            if (printTimeout) {
                clearTimeout(printTimeout);
            }
        };
    }, [receiptPolling, printTimeout]);

    // Получение суммы для выбранного способа оплаты
    const getCurrentAmount = useCallback(() => {
        return paymentAmounts[selectedMethod?.id] || '';
    }, [paymentAmounts, selectedMethod]);

    // Функция для проверки математической корректности бонусной скидки
    // Функция для проверки математической корректности бонусной скидки (целые числа)
    const validateBonusAmount = useCallback((bonusAmount, cartItems, totalAmount) => {
        if (bonusAmount <= 0) return true;

        // Проверяем, что это целое число
        if (!Number.isInteger(bonusAmount)) return false;

        // Повторяем логику бэка
        const originalTotal = cartItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);
        const targetTotal = originalTotal - bonusAmount;

        let accumulatedTotal = 0;
        cartItems.forEach(item => {
            const itemTotal = item.price * item.quantity;
            const proportion = itemTotal / originalTotal;
            const adjustedTotal = Math.round(targetTotal * proportion * 100) / 100;
            const adjustedUnitPrice = Math.round((adjustedTotal / item.quantity) * 100) / 100;
            const itemTotalAmount = Math.round((adjustedUnitPrice * item.quantity) * 100) / 100;
            accumulatedTotal += itemTotalAmount;
        });

        // Учитываем корректировку на 1 копейку
        const diff = Math.round((targetTotal - accumulatedTotal) * 100) / 100;
        if (Math.abs(diff) >= 0.01) {
            const firstItem = cartItems[0];
            const correctedUnitPrice = Math.round((firstItem.price - (bonusAmount * firstItem.price * firstItem.quantity / originalTotal) + diff / firstItem.quantity) * 100) / 100;
            const correctedTotal = Math.round((correctedUnitPrice * firstItem.quantity) * 100) / 100;
            accumulatedTotal = accumulatedTotal - Math.round((firstItem.price * firstItem.quantity) * 100) / 100 + correctedTotal;
        }

        const finalTotal = Math.round(accumulatedTotal * 100) / 100;
        const nonBonusTotal = Math.round((totalAmount - bonusAmount) * 100) / 100;

        return finalTotal === nonBonusTotal;
    }, []);

// Функция для поиска максимальной корректной суммы бонусов (только целые числа)
    const findMaxValidBonusAmount = useCallback((maxAmount, cartItems, totalAmount) => {
        let validAmount = Math.floor(maxAmount); // Округляем до целого вниз

        // Уменьшаем на 1 (целое число), пока не найдем корректную сумму
        while (validAmount > 0) {
            if (validateBonusAmount(validAmount, cartItems, totalAmount)) {
                return validAmount;
            }
            validAmount -= 1;
        }

        return 0;
    }, [validateBonusAmount]);

    // Обновление суммы для способа оплаты
    const updatePaymentAmount = useCallback((methodId, amount) => {
        // Проверяем максимальную сумму для карт
        const method = paymentMethods.find(m => m.id === methodId);
        if (method && method.type === 'card') {
            const numAmount = parseFloat(amount) || 0;
            if (numAmount > totalAmount) {
                messageApi.warning('Сумма платежа картой не может превышать сумму к оплате');
                return;
            }
        }

        // Проверяем максимальную сумму для баллов
        if (method && method.type === 'bonus') {
            const numAmount = parseFloat(amount) || 0;

            // Проверяем, что это целое число
            if (numAmount !== Math.floor(numAmount)) {
                messageApi.warning('Баллы можно списывать только целыми числами');
                return;
            }

            const maxTheoretical = Math.floor(Math.min(method.balance, totalAmount * 0.99));
            const maxBonusAmount = findMaxValidBonusAmount(maxTheoretical, cart, totalAmount);

            if (numAmount > maxBonusAmount) {
                if (numAmount > method.balance) {
                    messageApi.warning('Сумма баллов не может превышать баланс клиента');
                } else if (numAmount > totalAmount * 0.99) {
                    messageApi.warning('Баллами можно оплатить не более 99% от суммы покупки');
                } else {
                    messageApi.warning(`Максимальная математически корректная сумма баллов: ${maxBonusAmount}`);
                }
                return;
            }

            // Дополнительная проверка перед установкой
            if (!validateBonusAmount(numAmount, cart, totalAmount)) {
                messageApi.warning('Данная сумма баллов приведет к ошибке расчета');
                return;
            }
        }

        setPaymentAmounts(prev => ({
            ...prev,
            [methodId]: amount
        }));
        setEditedMethods(prev => new Set([...prev, methodId]));
    }, [paymentMethods, totalAmount, messageApi]);

    // Обработчик клавиатуры
    const handleKeypadPress = useCallback((value) => {
        if (!selectedMethod) return;

        const currentAmount = getCurrentAmount();

        if (value === 'backspace') {
            updatePaymentAmount(selectedMethod.id, currentAmount.slice(0, -1));
        } else if (value === 'clear') {
            updatePaymentAmount(selectedMethod.id, '');
        } else {
            const newAmount = currentAmount + value;
            updatePaymentAmount(selectedMethod.id, newAmount);
        }
    }, [selectedMethod, getCurrentAmount, updatePaymentAmount]);

    // Получение общей суммы оплат
    const getTotalPaidAmount = useCallback(() => {
        return Object.values(paymentAmounts).reduce((sum, amount) => {
            return sum + (parseFloat(amount) || 0);
        }, 0);
    }, [paymentAmounts]);

    // Обработчик быстрого добавления суммы
    const handleQuickAmount = useCallback((amount) => {
        if (!selectedMethod) return;

        const currentAmount = parseFloat(getCurrentAmount()) || 0;
        const newAmount = currentAmount + amount;

        // Для карт проверяем лимит
        if (selectedMethod.type === 'card' && newAmount > totalAmount) {
            messageApi.warning('Сумма платежа картой не может превышать сумму к оплате');
            return;
        }

        // Для баллов проверяем лимиты
        if (selectedMethod.type === 'bonus') {
            const maxTheoretical = Math.floor(Math.min(selectedMethod.balance, totalAmount * 0.99));
            const maxBonusAmount = findMaxValidBonusAmount(maxTheoretical, cart, totalAmount);

            if (newAmount > maxBonusAmount) {
                if (newAmount > selectedMethod.balance) {
                    messageApi.warning('Сумма баллов не может превышать баланс клиента');
                } else {
                    messageApi.warning('Баллами можно оплатить не более 99% от суммы покупки');
                }
                return;
            }
        }

        updatePaymentAmount(selectedMethod.id, newAmount.toString());
    }, [selectedMethod, getCurrentAmount, updatePaymentAmount, totalAmount, messageApi]);

    // Переключение способа оплаты
    const handleMethodChange = useCallback((method) => {
        if (!method || method.id === selectedMethod?.id) return;

        const previousSelected = selectedMethod;

        // Если предыдущий способ не был отредактирован, перекидываем сумму
        if (previousSelected && !editedMethods.has(previousSelected.id)) {
            const amountToTransfer = parseFloat(paymentAmounts[previousSelected.id]) || totalAmount;

            setPaymentAmounts(prev => {
                const newAmounts = { ...prev };

                // Убираем сумму с предыдущего способа
                delete newAmounts[previousSelected.id];

                // Устанавливаем сумму для нового способа (если он еще не имеет суммы)
                if (!newAmounts[method.id]) {
                    let maxAmount = amountToTransfer;

                    // Для карт ограничиваем сумму
                    if (method.type === 'card') {
                        maxAmount = Math.min(amountToTransfer, totalAmount);
                    }
                    // Для баллов ограничиваем по балансу и сумме к оплате
                    else if (method.type === 'bonus') {
                        const maxTheoretical = Math.floor(Math.min(amountToTransfer, method.balance, totalAmount * 0.99));
                        maxAmount = findMaxValidBonusAmount(maxTheoretical, cart, totalAmount);
                    }

                    newAmounts[method.id] = maxAmount.toString();
                }

                // Обнуляем другие способы того же типа (кроме отредактированных)
                paymentMethods.forEach(m => {
                    if (m.type === method.type && m.id !== method.id && !editedMethods.has(m.id)) {
                        delete newAmounts[m.id];
                    }
                });

                return newAmounts;
            });
        } else if (!paymentAmounts[method.id]) {
            // Если новый способ не имеет суммы и мы не перекидываем с предыдущего
            const totalPaid = getTotalPaidAmount();
            const remaining = totalAmount - totalPaid;

            if (remaining > 0) {
                let maxAmount = remaining;

                // Для карт ограничиваем сумму
                if (method.type === 'card') {
                    maxAmount = Math.min(remaining, totalAmount);
                }
                // Для баллов ограничиваем по балансу
                else if (method.type === 'bonus') {
                    const maxTheoretical = Math.floor(Math.min(remaining, method.balance, totalAmount * 0.99));
                    maxAmount = findMaxValidBonusAmount(maxTheoretical, cart, totalAmount);
                }

                setPaymentAmounts(prev => ({
                    ...prev,
                    [method.id]: maxAmount.toString()
                }));
            }
        }

        setSelectedMethod(method);
    }, [paymentAmounts, paymentMethods, totalAmount, selectedMethod, editedMethods, getTotalPaidAmount]);

    // Получение суммы сдачи (только для наличных)
    const getChangeAmount = useCallback(() => {
        const cashMethods = paymentMethods.filter(m => m.type === 'cash');
        const cashTotal = cashMethods.reduce((sum, method) => {
            return sum + (parseFloat(paymentAmounts[method.id]) || 0);
        }, 0);

        const totalPaid = getTotalPaidAmount();
        const change = totalPaid - totalAmount;

        // Сдача только если есть переплата наличными
        return cashTotal > 0 && change > 0 ? Math.min(change, cashTotal - (totalAmount - (totalPaid - cashTotal))) : 0;
    }, [paymentMethods, paymentAmounts, getTotalPaidAmount, totalAmount]);

    // Запуск процесса оплаты
    // Создание продажи (общая логика)
    const createSaleTransaction = useCallback(async (terminalPaymentDetails = null) => {
        try {
            const totalPaid = getTotalPaidAmount();
            const change = getChangeAmount();

            const saleData = {
                items: cart.map(item => ({
                    id: item.external_id,
                    total_amount: item.price * item.quantity,
                    unit_price: item.price,
                    name: item.name,
                    quantity: item.quantity,
                    ...(item.mark_code && { mark_code: item.mark_code }) // Добавь эту строку
                })),
                payments: (() => {
                    const cardMethods = paymentMethods.filter(m => m.type === 'card' && paymentAmounts[m.id]);
                    const cashMethods = paymentMethods.filter(m => m.type === 'cash' && paymentAmounts[m.id]);
                    const bonusMethods = paymentMethods.filter(m => m.type === 'bonus' && paymentAmounts[m.id]);

                    const totalCardAmount = cardMethods.reduce((sum, m) => sum + parseFloat(paymentAmounts[m.id]), 0);
                    const totalBonusAmount = bonusMethods.reduce((sum, m) => sum + parseFloat(paymentAmounts[m.id]), 0);

                    const payments = [];

                    // Добавляем карточные платежи
                    cardMethods.forEach(m => {
                        const paymentData = {
                            type: m.type,
                            sum: parseFloat(paymentAmounts[m.id]),
                            payment_method_id: m.id
                        };

                        // Если есть данные терминала для этого способа оплаты
                        if (terminalPaymentDetails && m.pos_type) {
                            paymentData.payment_details = terminalPaymentDetails;
                        }

                        payments.push(paymentData);
                    });

                    // Добавляем баллы
                    bonusMethods.forEach(m => {
                        payments.push({
                            type: 'bonus',
                            sum: parseFloat(paymentAmounts[m.id])
                        });
                    });

                    // Добавляем наличные платежи
                    cashMethods.forEach(m => {
                        const enteredAmount = parseFloat(paymentAmounts[m.id]);
                        let correctedAmount;

                        if (cardMethods.length === 0 && bonusMethods.length === 0) {
                            correctedAmount = Math.min(enteredAmount, totalAmount);
                        } else {
                            correctedAmount = Math.min(enteredAmount, totalAmount - totalCardAmount - totalBonusAmount);
                        }

                        payments.push({
                            type: m.type,
                            sum: Math.max(0, correctedAmount),
                            payment_method_id: m.id
                        });
                    });

                    return payments;
                })(),
                customer: {
                    tax_id: "",
                    id: customer?.id || null
                }
            };

            const saleResponse = await deviceSalesService.createSale(saleData, idempotencyKey);

            if (saleResponse.result === 'error' || saleResponse.status === 'error') {
                throw new Error(saleResponse.message || 'Ошибка при создании продажи');
            }

            if (saleResponse.status !== 'success') {
                throw new Error('Неожиданный статус ответа от сервера');
            }

            const receiptId = saleResponse.receipt?.id;
            if (!receiptId) {
                throw new Error('Не получен ID чека');
            }

            return { saleResponse, receiptId, totalPaid, change };
        } catch (error) {
            console.error('Ошибка при создании продажи:', error);
            throw error;
        }
    }, [getTotalPaidAmount, getChangeAmount, cart, paymentMethods, paymentAmounts, totalAmount, customer]);

    // Функция для обработки терминальных платежей
    const processTerminalPayment = useCallback(async () => {
        const terminalMethods = paymentMethods.filter(method =>
            paymentAmounts[method.id] &&
            method.pos_type &&
            !ignoreIntegration[method.id]
        );

        if (terminalMethods.length === 0) {
            return null;
        }

        // Пока поддерживаем только один терминальный способ оплаты
        const terminalMethod = terminalMethods[0];
        const amount = parseFloat(paymentAmounts[terminalMethod.id]);

        setTerminalProcessing(true);
        setTerminalStatus('Подготовка к оплате...');
        updatePayCardStatus(true);

        try {
            const result = await terminalIntegrationService.processPayment(
                {
                    posType: terminalMethod.pos_type,
                    amount: amount
                },
                (status) => {
                    setTerminalStatus(status);
                }
            );

            if (result.success) {
                setTerminalPaymentDetails(result.paymentDetails);
                return result.paymentDetails;
            } else {
                throw new Error(result.error || 'Ошибка терминального платежа');
            }
        } catch (error) {
            throw error;
        } finally {
            setTerminalProcessing(false);
            updatePayCardStatus(false);
        }
    }, [paymentMethods, paymentAmounts, ignoreIntegration]);

    // Запуск процесса оплаты
    const startPaymentProcess = useCallback(async () => {
        const totalPaid = getTotalPaidAmount();

        if (totalPaid <= 0) {
            messageApi.warning('Введите сумму оплаты');
            return;
        }

        if (totalPaid < totalAmount) {
            messageApi.warning('Недостаточно средств для оплаты');
            return;
        }

        if (!idempotencyKey) {
            setIdempotencyKey(Date.now().toString() + Math.random().toString(36).substr(2, 9));
        }

        // Проверяем есть ли терминальные способы оплаты
        const hasTerminalPayment = paymentMethods.some(method =>
            paymentAmounts[method.id] &&
            method.pos_type &&
            !ignoreIntegration[method.id]
        );

        try {
            let terminalPaymentDetails = null;

            // Если есть терминальный платеж - обрабатываем его
            if (hasTerminalPayment) {
                terminalPaymentDetails = await processTerminalPayment();
            }

            // Создаем продажу
            setSaleProcessing(true);
            setSaleStep('creating');

            const { saleResponse, receiptId, totalPaid: finalTotalPaid, change } = await createSaleTransaction(terminalPaymentDetails);

            // НОВАЯ ЛОГИКА: Сразу завершаем продажу без опроса чека и печати
            setSaleProcessing(false);
            onComplete({
                methods: paymentMethods.filter(m => paymentAmounts[m.id]).map(m => ({
                    ...m,
                    amount: parseFloat(paymentAmounts[m.id])
                })),
                totalAmount: finalTotalPaid,
                change: change,
                cart: cart
            });

            // СТАРАЯ ЛОГИКА (закомментирована, можно вернуть при необходимости):
            // // Переходим к печати чека
            // setSaleStep('printing');
            // setPrintTimer(10);
            // const timeout = setTimeout(() => {
            //     setPrintTimer(0);
            // }, 10000);
            // setPrintTimeout(timeout);
            //
            // // Опрос статуса чека
            // const checkReceiptStatus = async () => {
            //     if (receiptCheckInProgress) return;
            //
            //     setReceiptCheckInProgress(true);
            //
            //     try {
            //         const receiptResponse = await deviceSalesService.getReceipt(receiptId);
            //
            //         if (receiptResponse.result === 'error') {
            //             clearInterval(polling);
            //             setReceiptPolling(null);
            //             setSaleProcessing(false);
            //             setReceiptCheckInProgress(false);
            //             showReceiptErrorModal();
            //             return;
            //         }
            //
            //         if (receiptResponse.result === 'success') {
            //             if (receiptResponse.status === 'completed' && receiptResponse.receipt) {
            //                 clearInterval(polling);
            //                 setReceiptPolling(null);
            //                 setReceiptCheckInProgress(false);
            //
            //                 printReceiptUniversal(receiptResponse.receipt);
            //
            //                 setTimeout(() => {
            //                     setSaleProcessing(false);
            //                     onComplete({
            //                         methods: paymentMethods.filter(m => paymentAmounts[m.id]).map(m => ({
            //                             ...m,
            //                             amount: parseFloat(paymentAmounts[m.id])
            //                         })),
            //                         totalAmount: finalTotalPaid,
            //                         change: change,
            //                         cart: cart
            //                     });
            //                 }, 3000);
            //
            //             } else if (receiptResponse.status === 'failed' || receiptResponse.status === 'no_data') {
            //                 clearInterval(polling);
            //                 setReceiptPolling(null);
            //                 setSaleProcessing(false);
            //                 setReceiptCheckInProgress(false);
            //                 showReceiptErrorModal();
            //             }
            //         }
            //     } catch (receiptError) {
            //         console.error('Ошибка при получении чека:', receiptError);
            //         clearInterval(polling);
            //         setReceiptPolling(null);
            //         setSaleProcessing(false);
            //         setReceiptCheckInProgress(false);
            //         showReceiptErrorModal();
            //     }
            //
            //     setReceiptCheckInProgress(false);
            // };
            //
            // // Первая проверка сразу
            // checkReceiptStatus();
            //
            // // Установка интервала на 5 секунд
            // const polling = setInterval(checkReceiptStatus, 5000);
            //
            // setReceiptPolling(polling);

        } catch (error) {
            console.error('Ошибка при обработке платежа:', error);
            setSaleProcessing(false);
            setTerminalProcessing(false);

            // Показываем модал с ошибкой терминала или продажи
            if (error.message.includes('терминал')) {
                showTerminalErrorModal(error.message);
            } else {
                showSaleErrorModal(error.message);
            }
        }
    }, [getTotalPaidAmount, totalAmount, paymentMethods, paymentAmounts, ignoreIntegration, processTerminalPayment, createSaleTransaction, onComplete, cart, messageApi]);

    const showReceiptErrorModal = useCallback(() => {
        const errorModal = modal.error({
            icon: null,
            title: (
                <div style={{ textAlign: 'center', fontSize: '20px', fontWeight: 600, lineHeight: '24px' }}>
                    Ошибка печати чека
                </div>
            ),
            content: (
                <div style={{
                    textAlign: 'center',
                    fontSize: '16px',
                    marginTop: '32px',
                    marginBottom: '12px',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center'
                }}>
                    <div style={{ fontSize: '18px', marginBottom: '32px', marginTop: '8px' }}>
                        Не удалось напечатать чек. Продажа завершена успешно.
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', width: '100%' }}>
                        <Button
                            type="primary"
                            size="large"
                            style={{
                                height: '70px',
                                fontSize: '18px',
                                lineHeight: '23px',
                                letterSpacing: '-0.44',
                                fontWeight: '600'
                            }}
                            onClick={() => {
                                errorModal.destroy();
                                onComplete({
                                    methods: paymentMethods.filter(m => paymentAmounts[m.id]).map(m => ({
                                        ...m,
                                        amount: parseFloat(paymentAmounts[m.id])
                                    })),
                                    totalAmount: getTotalPaidAmount(),
                                    change: getChangeAmount(),
                                    cart: cart
                                });
                            }}
                        >
                            Понятно
                        </Button>
                    </div>
                </div>
            ),
            width: 450,
            centered: true,
            maskClosable: false,
            closable: false,
            footer: null,
            bodyStyle: {
                paddingLeft: 12,
                paddingTop: 0,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center'
            }
        });
    }, [modal, onComplete, paymentMethods, paymentAmounts, getTotalPaidAmount, getChangeAmount, cart]);

    //Модал ошибки терминала
    const showTerminalErrorModal = useCallback((errorMessage) => {
        const errorModal = modal.error({
            icon: null,
            title: (
                <div style={{ textAlign: 'center', fontSize: '20px', fontWeight: 600, lineHeight: '24px' }}>
                    Ошибка терминала
                </div>
            ),
            content: (
                <div style={{
                    textAlign: 'center',
                    fontSize: '16px',
                    marginTop: '32px',
                    marginBottom: '12px',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center'
                }}>
                    <div style={{ fontSize: '18px', marginBottom: '32px', marginTop: '8px' }}>
                        {errorMessage}
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', width: '100%' }}>
                        <Button
                            type="primary"
                            size="large"
                            style={{
                                height: '70px',
                                fontSize: '18px',
                                lineHeight: '23px',
                                letterSpacing: '-0.44',
                                fontWeight: '600'
                            }}
                            onClick={() => {
                                errorModal.destroy();
                                startPaymentProcess();
                            }}
                        >
                            Повторить
                        </Button>
                        <Button
                            size="large"
                            style={{
                                height: '70px',
                                fontSize: '18px',
                                lineHeight: '23px',
                                letterSpacing: '-0.44',
                                fontWeight: '600'
                            }}
                            onClick={() => {
                                errorModal.destroy();
                            }}
                        >
                            Отмена
                        </Button>
                    </div>
                </div>
            ),
            width: 450,
            centered: true,
            maskClosable: false,
            closable: false,
            footer: null,
            bodyStyle: {
                paddingLeft: 12,
                paddingTop: 0,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center'
            }
        });
    }, [modal, startPaymentProcess]);

    const showSaleErrorModal = useCallback((errorMessage) => {
        const errorModal = modal.error({
            icon: null,
            title: (
                <div style={{ textAlign: 'center', fontSize: '20px', fontWeight: 600, lineHeight: '24px' }}>
                    Ошибка создания продажи
                </div>
            ),
            content: (
                <div style={{
                    textAlign: 'center',
                    fontSize: '16px',
                    marginTop: '32px',
                    marginBottom: '12px',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center'
                }}>
                    <div style={{ fontSize: '18px', marginBottom: '32px', marginTop: '8px' }}>
                        {errorMessage || 'Не удалось создать продажу. Попробуйте еще раз.'}
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', width: '100%' }}>
                        <Button
                            type="primary"
                            size="large"
                            style={{
                                height: '70px',
                                fontSize: '18px',
                                lineHeight: '23px',
                                letterSpacing: '-0.44',
                                fontWeight: '600'
                            }}
                            onClick={() => {
                                errorModal.destroy();
                            }}
                        >
                            Понятно
                        </Button>
                    </div>
                </div>
            ),
            width: 450,
            centered: true,
            maskClosable: false,
            closable: false,
            footer: null,
            bodyStyle: {
                paddingLeft: 12,
                paddingTop: 0,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center'
            }
        });
    }, [modal]);

    // Обработчик отмены
    const handleCancel = useCallback(() => {
        if (terminalProcessing || saleProcessing) {
            return;
        }

        // Отменяем терминальный платеж если он активен
        if (terminalProcessId) {
            terminalIntegrationService.cancelPayment();
        }

        // Сбрасываем флаг pay_card при отмене
        updatePayCardStatus(false);

        onCancel();
    }, [onCancel, terminalProcessing, saleProcessing, terminalProcessId, updatePayCardStatus]);





    // Рендер интерфейса оплаты
    const renderPaymentInterface = () => {
        if (!selectedMethod) return null;

        const currentAmount = getCurrentAmount();
        const fixedDenominations = [100, 500, 1000];

        return (
            <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                {/* Поле ввода суммы */}
                <Input
                    size="large"
                    placeholder="Сумма к оплате"
                    value={currentAmount}
                    onChange={(e) => updatePaymentAmount(selectedMethod.id, e.target.value)}
                    style={{
                        fontSize: '24px',
                        textAlign: 'center',
                        marginBottom: 16,
                        height: '60px',
                    }}
                    suffix="₸"
                />

                {selectedMethod.type === 'card' && selectedMethod.pos_type && deviceCashierRoleService.hasRole('director_store')&& (
                    <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                        <span style={{ fontSize: '14px' }}>Игнорировать интеграцию</span>
                        <Switch
                            checked={ignoreIntegration[selectedMethod.id] || false}
                            onChange={(checked) => setIgnoreIntegration(prev => ({
                                ...prev,
                                [selectedMethod.id]: checked
                            }))}
                        />
                    </div>
                )}

                {/* Кнопки быстрого добавления для наличных и баллов */}
                {(selectedMethod.type === 'cash' || selectedMethod.type === 'bonus') && (
                    <Row gutter={8} style={{ marginBottom: 16 }}>
                        {fixedDenominations.map((amount, index) => (
                            <Col key={index} span={8}>
                                <Button
                                    block
                                    size="large"
                                    onClick={() => handleQuickAmount(amount)}
                                    style={{ height: '50px', fontSize: '16px', fontWeight: 600, lineHeight: '19px' }}
                                >
                                    +{amount}
                                </Button>
                            </Col>
                        ))}
                    </Row>
                )}

                {/* Spacer для прижатия клавиатуры к низу */}
                <div style={{ flex: 1 }}></div>

                {/* Клавиатура */}
                <NumbersKeypad onKeyPress={handleKeypadPress}/>
            </div>
        );
    };

    // Рендер интерфейса обработки
    const renderProcessingInterface = () => {
        let title = 'Обработка платежа';
        let description = 'Ожидание подтверждения оплаты...';

        if (terminalProcessing) {
            title = 'Обработка платежа';
            description = terminalStatus || 'Ожидание подтверждения оплаты...';
        } else if (saleProcessing) {
            if (saleStep === 'creating') {
                title = 'Завершаем продажу';
                description = 'Готовим данные для сервиса...';
            } else if (saleStep === 'printing') {
                title = 'Печатаем чек';
                description = 'Ожидание готовности чека...';
            }
        }

        return (
            <div style={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                height: '500px',
                textAlign: 'center'
            }}>
                <Spin size="large" style={{ marginBottom: 32 }} />
                <Title level={3} style={{ marginBottom: 16 }}>
                    {title}
                </Title>
                <Text style={{ marginBottom: 32, fontSize: '16px' }}>
                    {description}
                </Text>

                {/* Кнопка пропустить печать чека */}
                {saleProcessing && saleStep === 'printing' && printTimer === 0 && (
                    <Button
                        type="default"
                        size="large"
                        onClick={() => {
                            if (receiptPolling) {
                                clearInterval(receiptPolling);
                                setReceiptPolling(null);
                            }
                            setSaleProcessing(false);
                            onComplete({
                                methods: paymentMethods.filter(m => paymentAmounts[m.id]).map(m => ({
                                    ...m,
                                    amount: parseFloat(paymentAmounts[m.id])
                                })),
                                totalAmount: getTotalPaidAmount(),
                                change: getChangeAmount(),
                                cart: cart
                            });
                        }}
                        style={{ width: '150px', marginTop: '20px' }}
                    >
                        Пропустить
                    </Button>
                )}
            </div>
        );
    };

    const totalPaid = getTotalPaidAmount();
    const remaining = totalAmount - totalPaid;
    const change = getChangeAmount();

    if (terminalProcessing || saleProcessing) {
        return (
            <Modal
                title="Оплата"
                open={visible}
                footer={null}
                width={600}
                centered
                maskClosable={false}
                closable={false}
                style={{ userSelect: 'none' }}
            >
                {renderProcessingInterface()}
            </Modal>
        );
    }

    return (
        <Modal
            title="Оплата"
            open={visible}
            onCancel={handleCancel}
            footer={null}
            width={600}
            centered
            maskClosable={false}
            style={{ userSelect: 'none' }}
        >
            <Row gutter={24} style={{ height: '500px' }}>
                {/* Левый блок - интерфейс оплаты (ширина по клавиатуре) */}
                <Col style={{ width: '272px', height: '100%' }}>
                    <div style={{
                        height: '100%',
                        boxSizing: 'border-box'
                    }}>
                        {renderPaymentInterface()}
                    </div>
                </Col>

                {/* Правый блок - выбор способа оплаты */}
                <Col style={{ flex: 1, height: '100%' }}>
                    <div style={{
                        height: '100%',
                        boxSizing: 'border-box',
                        display: 'flex',
                        flexDirection: 'column'
                    }}>
                        {/* Список способов оплаты с вертикальным скроллом */}
                        <div style={{
                            flex: 1,
                            marginBottom: 20,
                            overflowY: 'auto',
                            paddingRight: '8px'
                        }}>
                            <Space direction="vertical" style={{ width: '100%' }} size={8}>
                                {paymentMethods.map((method) => {
                                    const isSelected = selectedMethod?.id === method.id;
                                    const hasAmount = paymentAmounts[method.id];
                                    const amount = parseFloat(paymentAmounts[method.id]) || 0;
                                    const isEdited = editedMethods.has(method.id);

                                    // Проверяем, можно ли выбрать этот способ
                                    const editedSameType = paymentMethods.find(m =>
                                        m.type === method.type &&
                                        m.id !== method.id &&
                                        editedMethods.has(m.id)
                                    );
                                    const isDisabled = editedSameType && !isEdited;

                                    return (
                                        <Button
                                            key={method.id}
                                            block
                                            size="large"
                                            type={isSelected ? 'primary' : 'default'}
                                            disabled={isDisabled}
                                            onClick={() => handleMethodChange(method)}
                                            style={{
                                                height: '70px',
                                                textAlign: 'left',
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'center',
                                                fontSize: '16px',
                                                fontWeight: 600,
                                                lineHeight: '19px',
                                                opacity: hasAmount ? 1 : (isDisabled ? 0.5 : 1)
                                            }}
                                        >
                                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                                                <span>{method.name}</span>
                                                {method.type === 'bonus' && (
                                                    <span style={{ fontSize: '12px' }}>
                        Доступно: {method.balance.toLocaleString()} Б
                    </span>
                                                )}
                                            </div>
                                            {hasAmount && (
                                                <span>{amount.toLocaleString()} ₸</span>
                                            )}
                                        </Button>
                                    );
                                })}
                            </Space>
                        </div>

                        {/* Информация о сумме */}
                        <Divider style={{ margin: '8px 0 8px 0' }} />
                        <div>
                            <div style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                marginBottom: '16px'
                            }}>
                                <Typography.Text style={{fontSize: '16px', fontWeight: 600, lineHeight: '19px'}}>К оплате</Typography.Text>
                                <Typography.Text style={{fontSize: '16px', fontWeight: 600, lineHeight: '19px'}}>{totalAmount.toLocaleString()} ₸</Typography.Text>
                            </div>

                            {totalPaid > 0 && (
                                <div style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    marginBottom: '16px'
                                }}>
                                    <Typography.Text style={{fontSize: '16px', fontWeight: 600, lineHeight: '19px'}}>Оплачено</Typography.Text>
                                    <Typography.Text style={{fontSize: '16px', fontWeight: 600, lineHeight: '19px'}}>{totalPaid.toLocaleString()} ₸</Typography.Text>
                                </div>
                            )}

                            {change > 0 && (
                                <div style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    marginBottom: '16px'
                                }}>
                                    <Typography.Text style={{fontSize: '16px', fontWeight: 600, lineHeight: '19px'}}>Сдача</Typography.Text>
                                    <Typography.Text style={{ fontSize: '16px', fontWeight: 600, lineHeight: '19px', color: '#52c41a' }}>
                                        {change.toLocaleString()} ₸
                                    </Typography.Text>
                                </div>
                            )}

                            {remaining > 0 && (
                                <div style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    marginBottom: '16px'
                                }}>
                                    <Typography.Text style={{fontSize: '16px', fontWeight: 600, lineHeight: '19px', color: '#ff4d4f'}}>Не хватает</Typography.Text>
                                    <Typography.Text style={{ fontSize: '16px', fontWeight: 600, lineHeight: '19px', color: '#ff4d4f' }}>
                                        {remaining.toLocaleString()} ₸
                                    </Typography.Text>
                                </div>
                            )}
                        </div>

                        {/* Кнопка завершения */}
                        <Button
                            type="primary"
                            size="large"
                            block
                            disabled={totalPaid < totalAmount || paymentMethods.some(method => {
                                const amount = parseFloat(paymentAmounts[method.id]) || 0;
                                return method.type === 'bonus' && amount > method.balance;
                            })}
                            onClick={startPaymentProcess}
                            style={{
                                height: '70px',
                                fontSize: '18px',
                                lineHeight: '23px',
                                letterSpacing: '-0.44',
                                fontWeight: '600'
                            }}
                        >
                            Готово
                        </Button>
                    </div>
                </Col>
            </Row>
        </Modal>
    );
};

export default SalePaymentModal;